/**
 * Version check with 24h cache.
 *
 * - On login: warns or auto-updates depending on config.autoUpdate
 * - On errors: hints from cache only (no network)
 * - On update: shows changelog between current and target version
 */

import fs from 'node:fs'
import path from 'node:path'
import os from 'node:os'
import { execSync } from 'node:child_process'
import { warn } from './output.js'

declare const __CLI_VERSION__: string

interface VersionCache {
  latest: string
  checkedAt: number
}

const CACHE_FILE = path.join(os.homedir(), '.arbi', 'version-cache.json')
const CACHE_TTL_MS = 24 * 60 * 60 * 1000 // 24 hours

function readCache(): VersionCache | null {
  try {
    const data = JSON.parse(fs.readFileSync(CACHE_FILE, 'utf8'))
    if (data.latest && data.checkedAt && Date.now() - data.checkedAt < CACHE_TTL_MS) {
      return data as VersionCache
    }
  } catch {
    // No cache or invalid
  }
  return null
}

function writeCache(latest: string): void {
  try {
    const dir = path.dirname(CACHE_FILE)
    if (!fs.existsSync(dir)) fs.mkdirSync(dir, { recursive: true, mode: 0o700 })
    fs.writeFileSync(CACHE_FILE, JSON.stringify({ latest, checkedAt: Date.now() }) + '\n')
  } catch {
    // Non-critical
  }
}

export function getLatestVersion(skipCache = false): string | null {
  if (!skipCache) {
    const cached = readCache()
    if (cached) return cached.latest
  }

  try {
    const latest = execSync('npm view @arbidocs/cli version 2>/dev/null', {
      encoding: 'utf8',
      timeout: 5000,
    }).trim()
    writeCache(latest)
    return latest
  } catch {
    return null
  }
}

export function getCurrentVersion(): string {
  return __CLI_VERSION__
}

// ── Changelog reading ───────────────────────────────────────────────────────

/**
 * Read CHANGELOG.md bundled in the installed @arbidocs/cli package
 * and extract sections between fromVersion (exclusive) and toVersion (inclusive).
 *
 * Call this AFTER npm install so the file on disk is from the new version.
 */
export function readChangelog(fromVersion: string, toVersion: string): string | null {
  try {
    const globalRoot = execSync('npm root -g', { encoding: 'utf8', timeout: 5000 }).trim()
    const changelogPath = path.join(globalRoot, '@arbidocs', 'cli', 'CHANGELOG.md')
    const text = fs.readFileSync(changelogPath, 'utf8')
    return extractSections(text, fromVersion, toVersion)
  } catch {
    return null
  }
}

/**
 * Parse CHANGELOG.md and return all ## sections from toVersion
 * down to (but not including) fromVersion.
 *
 * Filters out: compare-changes links, Chore sections, and Contributors.
 */
function extractSections(changelog: string, fromVersion: string, toVersion: string): string | null {
  const lines = changelog.split('\n')
  const sections: string[] = []
  let capturing = false
  let skipSection = false

  for (const line of lines) {
    // Match "## v0.3.3" style headers
    const match = line.match(/^## v(.+)/)
    if (match) {
      skipSection = false
      const version = match[1].trim()
      if (version === fromVersion) {
        // Reached the user's current version — stop
        break
      }
      // Start capturing from toVersion (or any version between to and from)
      if (version === toVersion || capturing) {
        capturing = true
      }
    }

    if (!capturing) continue

    // Skip [compare changes] links
    if (line.match(/^\[compare changes\]/)) continue

    // Skip Chore and Contributors ### sections (and their content)
    if (line.match(/^### .*(Chore|Contributors)/)) {
      skipSection = true
      continue
    }
    // A new ### section ends the skip
    if (skipSection && line.match(/^### /)) {
      skipSection = false
    }
    if (skipSection) continue

    sections.push(line)
  }

  // Collapse multiple blank lines into one
  const result = sections
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  return result || null
}

/**
 * Print changelog between two versions. Reads from the bundled CHANGELOG.md
 * in the installed package — call AFTER npm install.
 */
export function showChangelog(fromVersion: string, toVersion: string): void {
  const changelog = readChangelog(fromVersion, toVersion)
  if (changelog) {
    console.log('\n' + changelog)
  }
}

// ── Update checks ───────────────────────────────────────────────────────────

/**
 * Called after login. If auto-update is enabled, updates in place.
 * Otherwise warns with the standard prefix.
 */
export async function checkForUpdates(autoUpdate?: boolean): Promise<void> {
  try {
    const latest = getLatestVersion()
    if (!latest || latest === __CLI_VERSION__) return

    if (autoUpdate) {
      warn(`\nYour arbi version is out of date (${__CLI_VERSION__} → ${latest}). Updating...`)
      execSync('npm install -g @arbidocs/cli@latest', { stdio: 'inherit' })
      showChangelog(__CLI_VERSION__, latest)
      console.log(`Updated to ${latest}.`)
    } else {
      warn(
        `\nYour arbi version is out of date (${__CLI_VERSION__} → ${latest}).` +
          `\nRun "arbi update" to upgrade, or "arbi update auto" to always stay up to date.`
      )
    }
  } catch {
    // Silent
  }
}

const NAG_FILE = path.join(os.homedir(), '.arbi', 'last-update-hint.json')
const NAG_INTERVAL_MS = 24 * 60 * 60 * 1000 // 24h

function shouldShowNag(latest: string): boolean {
  // Skip in JSON / scripted paths — an agent piping output to jq shouldn't
  // have its stderr polluted on every error, and a once-a-day human hint is
  // plenty. Detect via env (set explicitly by long-running commands) OR by
  // sniffing `--json` in argv (cheap, covers every short-lived command path
  // including the error branches that drop into hintUpdateOnError).
  if (process.env.ARBI_JSON === '1') return false
  if (process.argv.includes('--json')) return false
  try {
    const data = JSON.parse(fs.readFileSync(NAG_FILE, 'utf8')) as {
      latest?: string
      shownAt?: number
    }
    if (
      data.latest === latest &&
      typeof data.shownAt === 'number' &&
      Date.now() - data.shownAt < NAG_INTERVAL_MS
    ) {
      return false
    }
  } catch {
    // No file yet — first nag of the day, fall through.
  }
  return true
}

function markNagShown(latest: string): void {
  try {
    const dir = path.dirname(NAG_FILE)
    if (!fs.existsSync(dir)) fs.mkdirSync(dir, { recursive: true, mode: 0o700 })
    fs.writeFileSync(NAG_FILE, JSON.stringify({ latest, shownAt: Date.now() }) + '\n')
  } catch {
    // Non-critical
  }
}

/**
 * On unexpected errors, hint from cache only (no network call). Gated to once
 * per day per latest-known version, and suppressed entirely when the caller
 * sets ARBI_JSON=1 (commands wrap their --json paths in that flag).
 */
export function hintUpdateOnError(): void {
  try {
    const cached = readCache()
    if (!cached || cached.latest === __CLI_VERSION__) return
    if (!shouldShowNag(cached.latest)) return
    warn(
      `Your arbi version is out of date (${__CLI_VERSION__} → ${cached.latest}). Run "arbi update".`
    )
    markNagShown(cached.latest)
  } catch {
    // Silent
  }
}
